import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Select, DatePicker, InputNumber } from 'antd';
import { ProPageHeader as PageHeader } from '@ant-design/pro-layout';
import { PlusOutlined, DeleteFilled } from '@ant-design/icons';
import CustomModal from '../../../components/common/CustomModal';
import { SortableTableHeader, DataTable } from '../../../components/common';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  getRound,
  getSport,
  removeRound,
  addRound,
  getSeason,
  API_URL,
} from '../../../store/slices/adminSlice';
import { useTableState, useModal, useDeleteConfirm, useDataLoading } from '../../../hooks';
import { requiredRule } from '../../../utils';
import moment, { Moment } from 'moment';
import toastr from 'toastr';
import axios from 'axios';

const { RangePicker } = DatePicker;
const { Option } = Select;

// TypeScript interfaces
interface RoundTypeData {
  id: number;
  name: string;
}

interface SportData {
  _id: string;
  sportname: string;
  startDate: string;
  endDate: string;
}

interface RoundData {
  _id: string;
  roundno: number;
  roundname: string;
  roundtype: string;
  startDate: string;
  endDate: string;
  sport: SportData;
  season?: any;
  isActive?: boolean;
}

interface RoundFormValues {
  sport: string;
  roundno: number;
  roundname: string;
  roundtype: string;
  date: [Moment, Moment];
}

const Round: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<RoundFormValues>();

  // Redux state
  const roundData = useAppSelector((state) => state.admin.roundData) as RoundData[];
  const totalCount = useAppSelector((state) => state.admin.roundCount);
  const sportData = useAppSelector((state) => state.admin.sportData) as unknown as SportData[];

  // Use custom hooks
  const { currentPage, sortOrder, changePage, sortData, setCurrentPage } = useTableState({
    fetchAction: getRound,
  });

  const { visible, itemId: roundId, showModal, hideModal } = useModal({ form });

  const { showDeleteConfirm } = useDeleteConfirm({
    deleteAction: removeRound,
    idParamName: 'roundId',
  });

  const [loading] = useDataLoading(roundData);

  // Local state for form-specific data
  const [sportId, setSportId] = useState<string>('');
  const [isRoundUpdate, setIsRoundUpdate] = useState<boolean>(false);
  const [roundtype, setRoundtype] = useState<string>('');
  const [isDateEdit, setIsDateEdit] = useState<boolean>(true);

  const roundTypeData: RoundTypeData[] = [
    { id: 1, name: 'Regular' },
    { id: 2, name: 'Playoffs' },
  ];

  // Load additional data on mount
  useEffect(() => {
    const obj1 = {
      count: 1000,
      isActive: true,
      page: 0,
    };
    dispatch(getSport(obj1));

    const SeasonObj = {
      isActive: true,
    };
    dispatch(getSeason(SeasonObj));
  }, [dispatch]);

  const handleShowModal = (
    id: string,
    roundno?: number,
    roundname?: string,
    roundtypeVal?: string,
    sport?: SportData,
    startDate?: string,
    endDate?: string,
    season?: any
  ) => {
    showModal(id);
    setSportId(sport?._id || '');

    if (id !== '') {
      setIsRoundUpdate(true);
      setIsDateEdit(false);
      form.resetFields();
      form.setFieldsValue({
        roundno: roundno,
        roundname: roundname,
        roundtype: roundtypeVal,
        sport: sport?._id,
        date: [moment(new Date(startDate!)), moment(new Date(endDate!))],
      });
    } else {
      setIsRoundUpdate(false);
      setIsDateEdit(false);
      form.resetFields();
      form.setFieldsValue({
        roundno: 1,
      });
    }
  };

  const handleMenuClick = (value: string) => {
    setSportId(value);
    setFieldsValue(value);
  };

  const handleRoundTypeClick = (value: string) => {
    setRoundtype(value);
  };

  const setFieldsValue = (sportIdValue: string) => {
    if (sportIdValue) {
      const sportRounds = roundData.filter(
        (round: RoundData) => round.sport._id === sportIdValue
      );

      if (sportRounds.length > 0) {
        const sortedRounds = [...sportRounds].sort(
          (a: RoundData, b: RoundData) => a.roundno - b.roundno
        );
        const round = sortedRounds[sortedRounds.length - 1];

        const sDate = new Date(round.endDate);
        sDate.setDate(sDate.getDate() + 1);
        const eDate = new Date(sDate);
        eDate.setDate(eDate.getDate() + 6);

        form.setFieldsValue({
          roundno: round.roundno + 1,
          date: [moment(sDate), moment(eDate)],
        });
      } else {
        form.setFieldsValue({
          roundno: 1,
        });
      }
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (isRoundUpdate) {
        const obj = {
          roundId: roundId,
          roundname: values.roundname,
          startDate: values.date[0].toISOString(),
          endDate: values.date[1].toISOString(),
        };

        const headers = {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('access_token') || '',
        };
        await axios.post(API_URL + 'round/updateRound', obj, { headers });
        toastr.success('Round updated successfully');
        dispatch(getRound({ page: currentPage, sortValue: '', sortOrder: '' }));
        setIsRoundUpdate(false);
        hideModal();
      } else {
        const obj = {
          roundno: values.roundno,
          roundname: values.roundname,
          roundtype: roundtype,
          sportId: sportId,
          startDate: values.date[0].toISOString(),
          endDate: values.date[1].toISOString(),
        };

        const startDateValue = new Date(values.date[0].toISOString());
        const endDateValue = new Date(values.date[1].toISOString());
        let seasonstartDate = 0;
        let seasonendDate = 0;

        sportData.forEach((x: SportData) => {
          if (x._id === sportId) {
            const seasonStart = new Date(x.startDate);
            const seasonEnd = new Date(x.endDate);
            seasonstartDate = seasonStart.getTime();
            seasonendDate = seasonEnd.getTime();
          }
        });

        if (
          seasonstartDate <= startDateValue.getTime() &&
          endDateValue.getTime() <= seasonendDate
        ) {
          dispatch(addRound(obj));
          hideModal();
          setCurrentPage(0);
        } else {
          toastr.warning('Please select date between sport date');
        }
      }
    } catch (error) {
      // Validation failed - form will show errors
    }
  };

  const handleMenuClickSport = (value: string) => {
    setSportId(value);
    if (value === '') {
      dispatch(getRound({ page: currentPage, sortValue: '', sortOrder: '' }));
    } else {
      dispatch(getRound({ sportId: value, page: 0 }));
    }
  };

  const sportOptions = sportData.map((x: SportData, index: number) => (
    <Option value={x._id} key={index}>
      {x.sportname}
    </Option>
  ));

  // Define table columns
  const columns = [
    {
      key: 'roundno',
      title: (
        <SortableTableHeader
          label="Round No"
          sortKey="roundno"
          currentSortOrder={sortOrder}
          onSort={sortData}
        />
      ),
      render: (item: RoundData) => item.roundno,
    },
    {
      key: 'roundname',
      title: (
        <SortableTableHeader
          label="Round Name"
          sortKey="roundname"
          currentSortOrder={sortOrder}
          onSort={sortData}
        />
      ),
      render: (item: RoundData) => item.roundname,
    },
    {
      key: 'roundtype',
      title: (
        <SortableTableHeader
          label="Type"
          sortKey="roundtype"
          currentSortOrder={sortOrder}
          onSort={sortData}
        />
      ),
      render: (item: RoundData) => item.roundtype,
    },
    {
      key: 'sport',
      title: (
        <SortableTableHeader
          label="Sports"
          sortKey="sport.sportname"
          currentSortOrder={sortOrder}
          onSort={sortData}
        />
      ),
      render: (item: RoundData) => item.sport.sportname,
    },
    {
      key: 'actions',
      title: 'Action',
      className: 'action-icons',
      render: (item: RoundData) => (
        <>
          <Button
            className="btn-sm mr-2"
            type="link"
            onClick={() =>
              handleShowModal(
                item._id,
                item.roundno,
                item.roundname,
                item.roundtype,
                item.sport,
                item.startDate,
                item.endDate,
                item.season
              )
            }
          >
            <i className="fa fa-pencil" style={{ color: '#666' }}></i>
          </Button>
          <Button
            className="btn-sm mr-2"
            type="link"
            onClick={() => showDeleteConfirm(item._id)}
          >
            <DeleteFilled style={{ color: '#666' }} />
          </Button>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="box box-default mb-4">
        <PageHeader
          prefixedClassName="ant-page-header"
          title="Round"
          subTitle=""
          extra={[
            <Select
              key="2"
              defaultValue=""
              placeholder="Season"
              style={{ width: 150 }}
              onChange={handleMenuClickSport}
              className="mobile-bottom-space"
            >
              <Option value="">All</Option>
              {sportOptions}
            </Select>,
            <Button
              key="1"
              type="default"
              onClick={() => handleShowModal('', undefined, '', '')}
            >
              <PlusOutlined className="mr-1" />
              Add Round
            </Button>,
          ]}
        />
        <DataTable
          columns={columns}
          data={roundData}
          loading={loading}
          currentPage={currentPage}
          pageSize={10}
          total={totalCount}
          onPageChange={changePage}
          sortOrder={sortOrder}
          onSort={sortData}
          rowKey="_id"
        />
        <CustomModal
          handleCancel={hideModal}
          handleOk={handleOk}
          visible={visible}
          title="Round"
          backBtnText="Back"
          submitBtnText="Submit"
        >
          <Form form={form} className="sport-form l_hgt0" layout="vertical">
            <Form.Item
              label="Select Sport"
              name="sport"
              rules={[{ required: true, message: 'Please select sport' }]}
            >
              <Select disabled={isRoundUpdate} onChange={handleMenuClick}>
                {sportOptions}
              </Select>
            </Form.Item>
            <Form.Item
              label="Round No"
              name="roundno"
              rules={[{ required: true, message: 'Please enter round no' }]}
            >
              <InputNumber disabled={isRoundUpdate} placeholder="Round No" />
            </Form.Item>
            <Form.Item
              label="Round Name"
              name="roundname"
              rules={[requiredRule('round name')]}
            >
              <Input placeholder="Round Name" />
            </Form.Item>
            <Form.Item
              label="Select Type"
              name="roundtype"
              rules={[{ required: true, message: 'Please select Type' }]}
            >
              <Select disabled={isRoundUpdate} onChange={handleRoundTypeClick}>
                {roundTypeData.map((x, index) => (
                  <Option value={x.name} key={index}>
                    {x.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Select start date and end date"
              name="date"
              rules={[{ required: true, message: 'Please enter date range' }]}
            >
              <RangePicker disabled={isDateEdit} />
            </Form.Item>
          </Form>
        </CustomModal>
      </div>
    </>
  );
};

export default Round;
